import { useEffect, useRef } from "react";
import { useFieldArray, useForm } from "react-hook-form";

import { useUpdateInvoiceMutation } from "../../store/services/invoices";
import { Invoice, Job } from "../../types/InvoiceTypes";
import debounce from "debounce";

export const useUpdateInvoiceForm = (invoice?: Invoice) => {
  const [updateInvoice, { isLoading, isSuccess, isError, error }] =
    useUpdateInvoiceMutation();

  const isUpdating = useRef(false);

  const {
    control,
    watch,
    formState: { isDirty, isValid, dirtyFields },
    reset,
    setValue,
  } = useForm<Invoice>({
    defaultValues: {
      number: invoice?.number || "",
      issued_at: invoice?.issued_at || "",
      due_at: invoice?.due_at || "",
      note: invoice?.note,
      currency: invoice?.currency,
      outgoing_currency: invoice?.outgoing_currency,
      show_transaction_data_on_public_view:
        invoice?.show_transaction_data_on_public_view || false,
      items_amounts_are_in_outgoing_currency:
        invoice?.items_amounts_are_in_outgoing_currency || false,
      items: invoice?.items?.length
        ? invoice.items
        : [
            {
              description: "",
              rate: 0,
            },
          ],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "items",
  });

  const watchItems = watch("items");

  useEffect(() => {
    const subscription = watch(
      debounce(async (newValues: any, { name }: any) => {
        if (invoice && !isLoading && isValid && !isUpdating.current) {
          isUpdating.current = true;
          const key = getFieldName(name);
          const newValue = getFieldValue(name, newValues[key]);

          const items = getItems(key, invoice.items || []);

          const payload = {
            // `currency` and `items_amounts_are_in_outgoing_currency` are
            // required by the payload amount re-expression in units.
            // They are expressed like attrs of `data` to not have a warning in
            // the dependencies array, but only when they properly change
            currency: newValues.currency,
            outgoing_currency: newValues.outgoing_currency,
            items_amounts_are_in_outgoing_currency:
              newValues.items_amounts_are_in_outgoing_currency,
            items,
            [key]: newValue,
            id: invoice.id,
          };

          const response = await updateInvoice(payload);

          if ("data" in response) {
            // Only reset for currency changes that affect calculations
            if (
              ["currency", "outgoing_currency"].includes(key) &&
              response.data.items?.some(
                (item, index) =>
                  JSON.stringify(item) !==
                  JSON.stringify(invoice?.items?.[index])
              )
            ) {
              // Mark all fields as not dirty before reset
              Object.keys(dirtyFields).forEach((field) => {
                const typedField = field as keyof Invoice;
                if (typedField in response.data) {
                  setValue(typedField, response.data[typedField], {
                    shouldDirty: false,
                  });
                }
              });

              isUpdating.current = false;

              // Reset the form with the new data
              reset(response.data, {
                keepDirty: false, // Don't keep any fields as dirty
              });
            } else {
              isUpdating.current = false;
            }
          } else {
            isUpdating.current = false;
          }
        }
      }, 300)
    );

    return () => subscription.unsubscribe();
  }, [
    watch,
    invoice,
    updateInvoice,
    isLoading,
    isDirty,
    isValid,
    reset,
    fields.length,
    dirtyFields,
    setValue,
  ]);

  // Effect triggered when items are added or removed
  useEffect(() => {
    if (invoice && watchItems && invoice.items?.length !== watchItems.length) {
      const payload = {
        // `currency` and `items_amounts_are_in_outgoing_currency` are
        // required by the payload amount re-expression in units.
        // They are expressed like attrs of `data` to not have a warning in
        // the dependencies array, but only when they properly change
        currency: invoice.currency,
        outgoing_currency: invoice.outgoing_currency,
        items_amounts_are_in_outgoing_currency:
          invoice.items_amounts_are_in_outgoing_currency,
        items: [...watchItems],
        id: invoice.id,
      };
      updateInvoice(payload);
    }
  }, [fields.length, invoice, updateInvoice, watchItems]);

  return {
    control,
    isLoading,
    isSuccess,
    isError,
    error,
    fields,
    append,
    remove,
  };
};

const isFieldDescriptionOrRate = (name = ""): boolean => {
  // Looks for the pattern `items.{index}.{description|rate}` in the
  // field name to support dynamic fields coming from the table
  return !!name.match(/items\.[0-9]*\.(description|rate)/g);
};

const getFieldName = (name: string) => {
  return isFieldDescriptionOrRate(name) ? "items" : name;
};

const isFieldRate = (name: string): boolean => {
  // Looks for the pattern `items.{index}.rate` in the field name to
  // force 2 digits rounding before sending to the backend
  return !!name?.match(/items\.[0-9]*\.rate/g);
};

const formatItemsRate = (items: Job[]) => {
  return items.map((item) => ({
    ...item,
    rate: Number(Number(item.rate).toFixed(2)),
  }));
};

const getFieldValue = (name: string, value: any) => {
  return isFieldRate(name) ? formatItemsRate(value as Job[]) : value;
};

const getItems = (key: string, items: Job[]) => {
  // Trigger items calculation when
  // `items_amounts_are_in_outgoing_currency`, `currency` or `outgoing_currency`
  // change
  if (
    [
      "items_amounts_are_in_outgoing_currency",
      "currency",
      "outgoing_currency",
    ].includes(key)
  ) {
    return items;
  }

  return undefined;
};
